/**
 * ORs integration markdown cohorts (Athena and AdvancedMD) into the three
 * Scribe rollout flags that gate the sectional-editor experience, so
 * integration users can be released independently of the Scribe rollout.
 *
 * Why this lives one layer above `@/features/flags` rather than inside it:
 * `useIsMarkdownSectionalEditor` (the editor-version resolver) imports the
 * onboarding-sessions query, which statically imports `scribeAPIV2.ts`, so
 * folding it back in creates a top-level import cycle. Same shape as
 * `src/hooks/useEnableBlockEditorV1.ts`.
 *
 * Deliberately does NOT resolve the provider via `useIntegrationEhrInfo`:
 * these hooks run on every `useBlockEditorMarkdown` / `useCopySettings`
 * consumer (Evidence chat input, template chatboxes, signup personalization
 * preview, ...), and that hook unconditionally mounts `GET /ehr/profile` +
 * the onboarding-sessions query — including a destructive "EHR connection
 * error" toast on surfaces that have no integration context. The resolver
 * takes no provider argument here; the AMD hook subscribes to the shared
 * cache with `enabled: false` so no extra network mounts from this module.
 *
 * The Athena arm is the backend's resolved editor decision
 * (`useResolvedEditorVersion`), not a local flag cohort — its sessions
 * query mounts for every possibly-Athena user (the determination is
 * backend-authoritative), so the shared cache entry arms fleet-wide, one
 * deduped request per app load.
 */
import { useIsAmbientCopilotEnabled } from '@/app-shell/flags/agents-flags';
import { resolveSectionsEnabled } from '@/components/tiptap/vMarkdown/resolveSectionsEnabled';
import { useIsMarkdownSectionalEditor } from '@/hooks/editor-version/useResolvedEditorVersion';
import { useAmdMarkdownSectionalEditorEnabled } from '@/hooks/integrations/useAmdMarkdownSectionalEditorEnabled';
import {
  useIsImprovedCopyActionsEnabled,
  useIsLowLatencyNoteEditingEnabled,
  useIsSectionsInNotesEnabled,
} from '@heidi/templates/flags';

/**
 * Whether the vMarkdown editor should register the SectionNode/Heading
 * extensions: the Scribe rollout flag OR an integration markdown cohort.
 */
export const useIsSectionsInNotesEnabledForEditor = (): boolean => {
  const scribeEnabled = useIsSectionsInNotesEnabled();
  const athenaEnabled = useIsMarkdownSectionalEditor();
  const amdEnabled = useAmdMarkdownSectionalEditorEnabled();
  return scribeEnabled || athenaEnabled || amdEnabled;
};

/**
 * Whether the "Separate sections" template toggle is meaningful for this user.
 * Same gate as the note editor's section extensions.
 * `release-scribe-enable-markdown-native-editor` does not grant the toggle;
 * that split gets its own flag.
 *
 * Kept separate from {@link useIsSectionsInNotesEnabledForEditor}, which also
 * drives vMarkdown extension registration and copy/wrapping.
 */
export const useIsSectionsInNotesEnabledForTemplates = (): boolean =>
  useIsSectionsInNotesEnabledForEditor();

/**
 * Whether the markdown block editor emits partial document updates instead of
 * reserialising the whole note per keystroke: the Scribe rollout flag OR an
 * integration markdown cohort.
 */
export const useIsLowLatencyNoteEditingEnabledForEditor = (): boolean => {
  const scribeEnabled = useIsLowLatencyNoteEditingEnabled();
  const athenaEnabled = useIsMarkdownSectionalEditor();
  const amdEnabled = useAmdMarkdownSectionalEditorEnabled();
  return scribeEnabled || athenaEnabled || amdEnabled;
};

/**
 * Whether section-scoped copy affordances are available: the Scribe rollout
 * flag OR an integration markdown cohort.
 */
export const useIsImprovedCopyActionsEnabledForEditor = (): boolean => {
  const scribeEnabled = useIsImprovedCopyActionsEnabled();
  const athenaEnabled = useIsMarkdownSectionalEditor();
  const amdEnabled = useAmdMarkdownSectionalEditorEnabled();
  return scribeEnabled || athenaEnabled || amdEnabled;
};

/**
 * Whether a section editor should wrap `# ` headings into section nodes (and
 * strip them on the way back out). Reactive twin of the section editor's own
 * {@link resolveSectionsEnabled} derivation with `enableSections: true`, so the
 * ambient-copilot flag turns wrapping on cohort-wide even when the base flag is
 * off, and a late-resolving flag re-renders the caller. Section editors call this
 * and pass the result as the `wrapSections` conversion option (forward + reverse);
 * the converter itself no longer reads any flag, so non-section callers never wrap.
 */
export const useSectionsWrappingEnabled = (): boolean =>
  resolveSectionsEnabled({
    enableSections: true,
    enableSectionParser: useIsSectionsInNotesEnabledForEditor(),
    ambientCopilotEnabled: useIsAmbientCopilotEnabled(),
  });
